import React, { useEffect } from "react";
import { FaGithub } from "react-icons/fa";
import { MdOpenInNew } from "react-icons/md";

const PROJECTS = [
    {
        id: 1,
        tag: "Observability",
        title: "Azure Monitoring Pipeline",
        problem:
            "Lack of visibility into microservice health and dependency chains led to slow incident detection and high mean time to resolution (MTTR) across enterprise workloads.",
        solution:
            "Built custom Log Analytics dashboards and automated alerting rules using Azure Monitor and Application Insights. Integrated telemetry across services to create a unified source of observability truth.",
        tech: ["Azure Monitor", "Application Insights", "KQL", "Log Analytics", "Alerting"],
        github: "https://github.com/ravirajkumartiwari",
    },
    {
        id: 2,
        tag: "Full-Stack",
        title: "Portfolio Web Application",
        problem:
            "Needed a professional, high-performance portfolio site to showcase cloud engineering work, technical skills, and blog-style engineering notes.",
        solution:
            "Built with React.js using a multi-page architecture, Stitch design system tokens, TailwindCSS, and deployed via GitHub. Implements dark-mode-first design with Inter typography and Azure-blue branding.",
        tech: ["React.js", "TailwindCSS", "React Router", "JavaScript", "GitHub Pages"],
        github: "https://github.com/ravirajkumartiwari",
    },
    {
        id: 3,
        tag: "Cost Optimization",
        title: "Cloud Cost Awareness Initiative",
        problem:
            "Unused and underutilized Azure resources in staging and monitoring environments were consuming budget without contributing to system reliability.",
        solution:
            "Audited Azure footprint using Azure Cost Management. Identified orphaned resources and inefficient configurations. Established tagging policies and cost dashboards for ongoing visibility.",
        tech: ["Azure Cost Management", "Azure Monitor", "Resource Tagging", "Cost Analysis"],
        github: "https://github.com/ravirajkumartiwari",
    },
];

function Projects() {
    useEffect(() => { document.title = "Projects — Raviraj Kumar Tiwari"; }, []);

    return (
        <div className="min-h-screen" style={{ background: "var(--clr-surface)" }}>
            {/* Page Header */}
            <section className="px-6 py-20 section-light">
                <div className="max-w-6xl mx-auto">
                    <p className="section-label mb-4">Engineering Work</p>
                    <h1 className="display-heading max-w-xl">Projects</h1>
                    <p className="mt-4 text-on-surface-variant max-w-2xl leading-relaxed">
                        A selection of cloud infrastructure, observability, and platform engineering
                        projects focused on scalability, 99.9% availability, and automated reliability.
                    </p>
                </div>
            </section>

            {/* Project Cards */}
            <section className="px-6 py-20" style={{ background: "var(--clr-surface)" }}>
                <div className="max-w-4xl mx-auto space-y-6">
                    {PROJECTS.map(({ id, tag, title, problem, solution, tech, github }) => (
                        <div key={id} className="card-node" style={{ background: "var(--clr-surface-lowest)" }}>
                            <div className="flex items-start justify-between gap-4 mb-6">
                                <div>
                                    <span className="chip chip-primary mb-3 inline-block">{tag}</span>
                                    <h2 className="text-xl font-semibold text-on-surface">{title}</h2>
                                </div>
                                <a
                                    href={github}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={`${title} repository`}
                                    className="text-on-surface-variant hover:text-primary transition-colors flex-shrink-0 mt-1"
                                >
                                    <FaGithub size={20} />
                                </a>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-6 mb-6">
                                <div
                                    className="rounded-[4px] p-4"
                                    style={{ background: "var(--clr-surface-container)" }}
                                >
                                    <p className="section-label mb-2">The Problem</p>
                                    <p className="text-sm text-on-surface-variant leading-relaxed">{problem}</p>
                                </div>
                                <div
                                    className="rounded-[4px] p-4"
                                    style={{ background: "var(--clr-surface-container)" }}
                                >
                                    <p className="section-label mb-2">The Solution</p>
                                    <p className="text-sm text-on-surface-variant leading-relaxed">{solution}</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {tech.map((t) => (
                                    <span key={t} className="chip">{t}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 py-16 section-container">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                        <p className="headline text-xl">Interested in collaborating?</p>
                        <p className="text-sm text-on-surface-variant mt-2">
                            Open to cloud monitoring, Azure migrations, and observability consulting.
                        </p>
                    </div>
                    <a
                        href="https://github.com/ravirajkumartiwari"
                        target="_blank"
                        rel="noreferrer"
                        className="btn-secondary flex-shrink-0"
                    >
                        <MdOpenInNew size={16} /> View GitHub
                    </a>
                </div>
            </section>
        </div>
    );
}

export default Projects;
